import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useDispatch,
  useDispatchInvitations,
  useCancelDispatch,
  useResendInvitation,
} from '../../hooks/useSurveys';
import { surveyService } from '../../services/survey.service';
import api from '../../services/api';
import type { InvitationStatus, DispatchStatus } from '../../types';
import { format } from 'date-fns';
import { ArrowLeftIcon, XCircleIcon, ArrowPathIcon, EyeIcon } from '@heroicons/react/24/outline';

const statusColors: Record<DispatchStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  scheduled: 'bg-blue-100 text-blue-800',
  sending: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  failed: 'bg-red-100 text-red-800',
};

const invitationStatusColors: Record<InvitationStatus, string> = {
  pending: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  delivered: 'bg-indigo-100 text-indigo-800',
  opened: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  bounced: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-600',
};

export default function DispatchDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: dispatch, isLoading: dispatchLoading, error: dispatchError } = useDispatch(id || '');
  const { data: invitations, isLoading: invitationsLoading } = useDispatchInvitations(id || '');
  const cancelDispatch = useCancelDispatch();
  const resendInvitation = useResendInvitation();

  // Backend embeds invitations in dispatch response; use them as fallback
  const resolvedInvitations = invitations ?? (dispatch as any)?.invitations ?? [];

  const handleSend = async () => {
    if (id && window.confirm('Send this dispatch to all selected contacts?')) {
      try {
        await surveyService.sendDispatch(id);
        window.location.reload();
      } catch (e) {
        alert('Failed to send dispatch');
      }
    }
  };

  const handleCancel = () => {
    if (id && window.confirm('Are you sure you want to cancel this dispatch?')) {
      cancelDispatch.mutate(id);
    }
  };

  const handleResend = (invitationId: string) => {
    if (window.confirm('Resend this invitation?')) {
      resendInvitation.mutate(invitationId);
    }
  };

  const [responseModal, setResponseModal] = useState<any>(null);
  const [loadingResponse, setLoadingResponse] = useState(false);

  const handleViewResponse = async (invitationId: string) => {
    setLoadingResponse(true);
    try {
      const res = await api.get(`/surveys/responses/${invitationId}`);
      setResponseModal(res.data);
    } catch {
      alert('Failed to load response');
    } finally {
      setLoadingResponse(false);
    }
  };

  if (dispatchLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (dispatchError || !dispatch) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load dispatch details.</p>
        <button
          onClick={() => navigate('/surveys/dispatches')}
          className="mt-2 text-sm text-indigo-600 hover:underline"
        >
          Back to dispatches
        </button>
      </div>
    );
  }

  const canCancel = ['draft', 'scheduled', 'sending', 'sent'].includes(dispatch.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/surveys/dispatches')}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Back"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Dispatch Details
            </h1>
            <p className="text-sm text-gray-500">
              {dispatch.template_name} · {dispatch.project_name || 'No project'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dispatch.status === 'draft' && (
            <button
              onClick={handleSend}
              className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
            >
              Send Now
            </button>
          )}
          {canCancel && (
            <button
              onClick={handleCancel}
              disabled={cancelDispatch.isPending}
              className="inline-flex items-center gap-2 rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 shadow-sm hover:bg-red-50 disabled:opacity-50"
            >
              <XCircleIcon className="h-4 w-4" />
              Cancel Dispatch
          </button>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <dt className="text-sm text-gray-500">Status</dt>
          <dd className="mt-1">
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusColors[dispatch.status]}`}>
              {dispatch.status}
            </span>
          </dd>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <dt className="text-sm text-gray-500">Response Rate</dt>
          <dd className="mt-1 text-lg font-semibold text-gray-900">
            {(dispatch.response_rate ?? 0) > 0 ? `${dispatch.response_rate}%` : '—'}
          </dd>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <dt className="text-sm text-gray-500">Completed</dt>
          <dd className="mt-1 text-lg font-semibold text-gray-900">
            {dispatch.completed_count ?? 0} / {dispatch.total_invitations ?? 0}
          </dd>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <dt className="text-sm text-gray-500">Average Score</dt>
          <dd className="mt-1 text-lg font-semibold text-gray-900">
            {dispatch.average_score != null ? Number(dispatch.average_score).toFixed(1) : '—'}
          </dd>
        </div>
      </div>

      {/* Dispatch Details */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-gray-500">Template</dt>
            <dd className="font-medium text-gray-900">{dispatch.template_name || '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Project</dt>
            <dd className="font-medium text-gray-900">{dispatch.project_name || '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Client</dt>
            <dd className="font-medium text-gray-900">{dispatch.client_name || '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Created</dt>
            <dd className="font-medium text-gray-900">
              {dispatch.created_at ? format(new Date(dispatch.created_at), 'MMM d, yyyy HH:mm') : '—'}
            </dd>
          </div>
          {dispatch.scheduled_at && (
            <div>
              <dt className="text-gray-500">Scheduled At</dt>
              <dd className="font-medium text-gray-900">
                {format(new Date(dispatch.scheduled_at), 'MMM d, yyyy HH:mm')}
              </dd>
            </div>
          )}
          {dispatch.sent_at && (
            <div>
              <dt className="text-gray-500">Sent At</dt>
              <dd className="font-medium text-gray-900">
                {format(new Date(dispatch.sent_at), 'MMM d, yyyy HH:mm')}
              </dd>
            </div>
          )}
          {dispatch.expires_at && (
            <div>
              <dt className="text-gray-500">Expires At</dt>
              <dd className="font-medium text-gray-900">
                {format(new Date(dispatch.expires_at), 'MMM d, yyyy HH:mm')}
              </dd>
            </div>
          )}
          {dispatch.reminder_days && dispatch.reminder_days.length > 0 && (
            <div>
              <dt className="text-gray-500">Reminders</dt>
              <dd className="font-medium text-gray-900">
                After {dispatch.reminder_days.join(', ')} days
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Invitations Table */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Invitations</h2>

        {invitationsLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
          </div>
        ) : resolvedInvitations && resolvedInvitations.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Sent
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Completed
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Reminders
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {resolvedInvitations.map((invitation: any) => (
                  <tr key={invitation.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {invitation.contact_name || `${invitation.first_name ?? ''} ${invitation.last_name ?? ''}`.trim() || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {invitation.contact_email || invitation.email || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${invitationStatusColors[invitation.status as InvitationStatus] ?? 'bg-gray-100 text-gray-800'}`}
                      >
                        {invitation.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {invitation.sent_at
                        ? format(new Date(invitation.sent_at), 'MMM d, HH:mm')
                        : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {invitation.completed_at
                        ? format(new Date(invitation.completed_at), 'MMM d, HH:mm')
                        : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {invitation.reminder_count ?? 0}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                      {['sent', 'delivered', 'opened'].includes(invitation.status) && (
                        <button
                          onClick={() => handleResend(invitation.id)}
                          disabled={resendInvitation.isPending}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Resend invitation"
                        >
                          <ArrowPathIcon className="h-4 w-4 inline" />
                        </button>
                      )}
                      {invitation.status === 'completed' && (
                        <button
                          onClick={() => handleViewResponse(invitation.id)}
                          disabled={loadingResponse}
                          className="text-green-600 hover:text-green-900 ml-2"
                          title="View answers"
                        >
                          <EyeIcon className="h-4 w-4 inline" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No invitations found.</p>
        )}
      </div>

      {/* Response Modal */}
      {responseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setResponseModal(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Survey Response — {responseModal.first_name} {responseModal.last_name}
              </h3>
              <button onClick={() => setResponseModal(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="mb-3 text-sm text-gray-500">
              CSAT Score: <span className="font-semibold text-gray-900">{responseModal.csat_score}%</span>
              {responseModal.completed_at && (
                <> · Completed: {format(new Date(responseModal.completed_at), 'MMM d, yyyy HH:mm')}</>
              )}
            </div>
            <div className="space-y-4">
              {(responseModal.questions || []).map((question: any, idx: number) => {
                const answer = Array.isArray(responseModal.answers)
                  ? responseModal.answers.find((a: any) => a.question_id === question.id)
                  : null;
                return (
                  <div key={question.id || idx} className="border-b pb-3">
                    <p className="text-sm font-medium text-gray-700">
                      {idx + 1}. {question.question_text}
                      <span className="ml-2 text-xs text-gray-400">({question.question_type})</span>
                    </p>
                    <p className="mt-1 text-sm text-gray-900">
                      {answer ? String(answer.answer_value ?? answer.value ?? '—') : <span className="text-gray-400">No answer</span>}
                    </p>
                  </div>
                );
              })}
              {(!responseModal.questions || responseModal.questions.length === 0) && responseModal.answers && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Raw Answers:</p>
                  {(Array.isArray(responseModal.answers) ? responseModal.answers : []).map((a: any, idx: number) => (
                    <div key={idx} className="text-sm text-gray-900 border-b pb-2 mb-2">
                      Question ID: {a.question_id} → <strong>{String(a.answer_value ?? a.value ?? '—')}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
